import { DoNotDisturb, ExpandLess, ExpandMore, PictureAsPdf } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import type { CellContext, Row } from '@tanstack/react-table';
import type {
  CancelProductCallback,
  CancelProductPredicate,
  OpenRefillPrescriptionUrl,
  PrescriptionMedicationsTableRow,
} from './PrescriptionMedicationsTable.types';
import { DISABLE_CANCEL, HIDE_CANCEL } from './PrescriptionMedicationsTable.types';

export const applyRowStyles = (item: Row<unknown>) => {
  const rowData = (item as Row<PrescriptionMedicationsTableRow>).original;
  const borderBottomWidth = rowData.removeBorder ? '0' : undefined;

  return {
    bgcolor: rowData.altRowColor ? 'action.hover' : undefined,
    td: {
      borderBottomWidth,
    },
  };
};

export const formatCenteredCell = (cell: React.ReactNode) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {cell}
    </Box>
  );
};

export const formatStatusCell = (item: CellContext<PrescriptionMedicationsTableRow, string | null>) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        fontWeight: item.row.original.expanded ? 'bold' : 'normal',
      }}
    >
      {item.row.original.status}{' '}
    </Box>
  );
};

export const formatProductNameCell = (
  item: CellContext<PrescriptionMedicationsTableRow, string | null>,
  onToggle: (id: number) => void,
) => {
  const expandIcon = item.row.original.expanded ? <ExpandLess /> : <ExpandMore />;

  const onClick = () => {
    onToggle(item.row.original.id);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        fontWeight: item.row.original.expanded ? 'bold' : 'normal',
      }}
    >
      {item.row.original.name}{' '}
      {item.row.original.refillPrescribedProducts.length > 0 && <IconButton onClick={onClick}>{expandIcon}</IconButton>}
    </Box>
  );
};

const getTooltipMessage = ({
  hasCancelPermissions,
  isDisabled,
}: {
  hasCancelPermissions?: boolean;
  isDisabled?: boolean;
}) => {
  if (hasCancelPermissions) {
    return isDisabled ? 'Medication already cancelled' : 'Cancel medication';
  }
  return 'Cancel is restricted to prescribing clinicians';
};

export const formatActionCell = (
  item: CellContext<PrescriptionMedicationsTableRow, unknown>,
  onCancel?: CancelProductCallback,
  isProductCancellable?: CancelProductPredicate,
  openRefillPrescriptionUrl?: OpenRefillPrescriptionUrl,
  hasCancelPermissions?: boolean,
) => {
  const onClick = () => {
    onCancel?.(item.row.original.productId!, item.row.original?.name ?? '');
  };

  const onclickViewRefillScript = () => {
    openRefillPrescriptionUrl?.(item.row.original.id);
  };

  const isRefillScript = item.row.original.isRefillScript;
  const actionState = isProductCancellable?.(item.row.original) ?? HIDE_CANCEL;
  const isDisabled = actionState === DISABLE_CANCEL;
  const iconColor = isDisabled ? 'disabled' : 'error';

  const cancelTooltip = getTooltipMessage({ hasCancelPermissions, isDisabled });

  if (isRefillScript) {
    return formatCenteredCell(
      <Tooltip title="View Refill Script" placement="bottom">
        <IconButton onClick={onclickViewRefillScript} aria-label="View Refill Script">
          <PictureAsPdf color={'primary'} />
        </IconButton>
      </Tooltip>,
    );
  }

  if (actionState === HIDE_CANCEL) {
    return null;
  }

  return formatCenteredCell(
    <Tooltip title={cancelTooltip}>
      <span>
        <IconButton onClick={onClick} disabled={isDisabled} aria-label="Cancel Prescription">
          <DoNotDisturb color={iconColor} />
        </IconButton>
      </span>
    </Tooltip>,
  );
};
